"use client";
import { hasSupplementalUiCopy } from "@/config/locale";
import { type ReactNode, useState, createContext, useContext, useEffect } from "react";
import { Menu, Truck, HelpCircle, MessageSquare, ArrowRight, Heart } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { getCategoryIcon } from "./mega-menu-panels";
import { BrandHomeLink } from "../../shared/brand-wordmark";
import { useHeaderScrollHideSuppression } from "../../header-scroll-shadow";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
	SheetCloseButton,
} from "@/ui/components/ui/sheet";
import { LinkWithChannel } from "@/ui/atoms/link-with-channel";
import type { CategoryItem } from "./mega-menu-panels";

const MobileMenuContext = createContext<{ close: () => void } | null>(null);

export const useMobileMenuClose = () => {
	const context = useContext(MobileMenuContext);
	return context?.close ?? (() => {});
};

interface MobileMenuLabels {
	peptides: string;
	research: string;
	support: string;
	favorites: string;
	browseAll: string;
	allPeptides: string;
	guide: string;
	journal: string;
	science: string;
	applications: string;
	ruo: string;
	dossier: string;
	inquiry: string;
	partners: string;
	shipping: string;
	faq: string;
	contact: string;
}

type Props = {
	campaignLink?: ReactNode;
	categories?: CategoryItem[];
	labels?: MobileMenuLabels;
};

function SectionHeading({ children }: { children: ReactNode }) {
	return (
		<p className="px-4 pb-1.5 pt-5 text-center font-mono text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-primary">
			{children}
		</p>
	);
}

function NavLink({
	href,
	icon: Icon,
	children,
}: {
	href: string;
	icon: React.ComponentType<{ className?: string }>;
	children: ReactNode;
}) {
	return (
		<LinkWithChannel
			href={href}
			className="hover:border-primary-hero/20 hover:bg-primary-hero/[0.04] group mx-3 my-0.5 flex min-h-[46px] items-center gap-3 rounded-xl border border-transparent px-3 font-display text-[0.92rem] font-medium tracking-[-0.005em] text-foreground transition-colors hover:text-primary-hero"
		>
			<span className="bg-primary-hero/[0.08] group-hover:bg-primary-hero/[0.15] flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors">
				<Icon className="h-3.5 w-3.5 text-primary-hero" />
			</span>
			<span className="flex-1">{children}</span>
			<ArrowRight className="text-muted-foreground/40 group-hover:translate-x-0.5 group-hover:text-primary-hero h-3.5 w-3.5 transition-all" />
		</LinkWithChannel>
	);
}

export const MobileMenu = ({ categories, labels, campaignLink }: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const close = () => setIsOpen(false);
	const t = useTranslations("nav");
	const tClose = useTranslations("localeChannel");
	const hasLocalizedCloseLabel = hasSupplementalUiCopy(useLocale());
	const suppressScrollHide = useHeaderScrollHideSuppression();

	useEffect(() => {
		suppressScrollHide(isOpen);
		if (!isOpen) return;
		const original = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = original;
			suppressScrollHide(false);
		};
	}, [isOpen, suppressScrollHide]);

	return (
		<Sheet open={isOpen} onOpenChange={setIsOpen}>
			<SheetTrigger asChild>
				<button
					type="button"
					className="hover:border-primary/30 hover:bg-primary/5 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-transparent text-foreground transition-colors hover:text-primary lg:hidden lg:h-10 lg:w-10"
					aria-label={t("openMenuAria")}
				>
					<Menu className="h-5 w-5" />
				</button>
			</SheetTrigger>
			<SheetContent side="left" className="flex w-full flex-col p-0 sm:max-w-sm">
				<SheetTitle className="sr-only">{t("menuTitle")}</SheetTitle>
				<SheetHeader className="relative flex items-center justify-center border-b border-border px-4 py-10">
					<BrandHomeLink className="[&_img]:h-40 [&_img]:w-auto" />
					<SheetCloseButton
						className="absolute right-3 top-1/2 -translate-y-1/2"
						aria-label={hasLocalizedCloseLabel ? tClose("close") : undefined}
					/>
				</SheetHeader>

				<MobileMenuContext.Provider value={{ close }}>
					<nav
						className="flex-1 overflow-y-auto py-2"
						onClick={(e) => {
							const anchor = (e.target as HTMLElement).closest("a");
							if (anchor) {
								// Defer close so router can navigate first (Radix Sheet closing too early swallowed clicks)
								setTimeout(close, 50);
							}
						}}
					>
						<div className="px-4">{campaignLink}</div>
						{/* Search bar entfernt — direkt zu Login + Peptide-CTA */}

						{categories && labels && (
							<>
								<div className="px-4 pt-3">
									<LinkWithChannel
										href="/login"
										className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-primary-hero px-6 py-2 text-center font-mono text-[0.78rem] font-bold uppercase leading-snug tracking-[0.22em] text-primary-hero-foreground shadow-lg transition-colors hover:bg-primary"
									>
										{t("accountEntry")}
										<ArrowRight className="h-5 w-5 shrink-0" />
									</LinkWithChannel>
									<LinkWithChannel
										href="/affiliate/login"
										className="mt-1.5 flex items-center justify-center gap-1.5 font-mono text-[0.58rem] font-medium uppercase tracking-[0.16em] text-muted-foreground transition-colors hover:text-primary-hero"
									>
										<span aria-hidden className="bg-muted-foreground/40 block h-px w-3" />
										{t("partnerEntry")}
										<ArrowRight className="h-3 w-3" />
									</LinkWithChannel>
								</div>

								<div className="px-4 pt-2">
									<LinkWithChannel
										href="/peptides"
										className="hover:bg-primary-hero/5 inline-flex h-10 w-full items-center justify-center gap-2 rounded-full border border-border bg-background font-mono text-[0.62rem] font-medium uppercase tracking-[0.18em] text-muted-foreground shadow-sm transition-colors hover:border-primary-hero hover:text-foreground"
									>
										{hasLocalizedCloseLabel ? labels.peptides : "Peptide"}
										<ArrowRight className="h-3.5 w-3.5" />
									</LinkWithChannel>
								</div>

								{/* Peptides — Kategorien (Section-Heading entfernt, Pill oben sagt schon "Peptide") */}
								{categories.map((cat) => {
									const Icon = getCategoryIcon(cat.slug);
									return (
										<NavLink key={cat.id} href={`/categories/${cat.slug}`} icon={Icon}>
											{cat.name}
										</NavLink>
									);
								})}

								<hr className="mx-4 my-2 border-border" />

								{/* Research / Wissen — als Pills (analog zum Peptide-CTA oben) */}
								<SectionHeading>{labels.research}</SectionHeading>
								<div className="space-y-2 px-4 pt-1">
									<LinkWithChannel
										href="/journal"
										className="border-primary-dark/60 before:via-accent/30 group relative inline-flex h-11 w-full items-center justify-center gap-2 overflow-hidden rounded-full border bg-white/70 font-mono text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-primary-dark shadow-[0_3px_10px_-4px_rgba(8,39,74,0.12)] backdrop-blur-md transition-all before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:to-transparent before:transition-transform before:duration-1000 hover:border-primary hover:bg-white/85 hover:before:translate-x-full"
									>
										{hasLocalizedCloseLabel ? labels.journal : "Studien"}
										<ArrowRight className="h-4 w-4" />
									</LinkWithChannel>
									<LinkWithChannel
										href="/science"
										className="border-primary-dark/60 before:via-accent/30 group relative inline-flex h-11 w-full items-center justify-center gap-2 overflow-hidden rounded-full border bg-white/70 font-mono text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-primary-dark shadow-[0_3px_10px_-4px_rgba(8,39,74,0.12)] backdrop-blur-md transition-all before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:to-transparent before:transition-transform before:duration-1000 hover:border-primary hover:bg-white/85 hover:before:translate-x-full"
									>
										{labels.science}
										<ArrowRight className="h-4 w-4" />
									</LinkWithChannel>
									<LinkWithChannel
										href="/applications"
										className="border-primary-dark/60 before:via-accent/30 group relative inline-flex h-11 w-full items-center justify-center gap-2 overflow-hidden rounded-full border bg-white/70 font-mono text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-primary-dark shadow-[0_3px_10px_-4px_rgba(8,39,74,0.12)] backdrop-blur-md transition-all before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:to-transparent before:transition-transform before:duration-1000 hover:border-primary hover:bg-white/85 hover:before:translate-x-full"
									>
										{labels.applications}
										<ArrowRight className="h-4 w-4" />
									</LinkWithChannel>
									<LinkWithChannel
										href="/research-use-only"
										className="border-primary-dark/60 before:via-accent/30 group relative inline-flex h-11 w-full items-center justify-center gap-2 overflow-hidden rounded-full border bg-white/70 font-mono text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-primary-dark shadow-[0_3px_10px_-4px_rgba(8,39,74,0.12)] backdrop-blur-md transition-all before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:to-transparent before:transition-transform before:duration-1000 hover:border-primary hover:bg-white/85 hover:before:translate-x-full"
									>
										{labels.ruo}
										<ArrowRight className="h-4 w-4" />
									</LinkWithChannel>
									<LinkWithChannel
										href="/dossier"
										className="border-primary-dark/60 before:via-accent/30 group relative inline-flex h-11 w-full items-center justify-center gap-2 overflow-hidden rounded-full border bg-white/70 font-mono text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-primary-dark shadow-[0_3px_10px_-4px_rgba(8,39,74,0.12)] backdrop-blur-md transition-all before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:to-transparent before:transition-transform before:duration-1000 hover:border-primary hover:bg-white/85 hover:before:translate-x-full"
									>
										{labels.dossier}
										<ArrowRight className="h-4 w-4" />
									</LinkWithChannel>
									<LinkWithChannel
										href="/partners"
										className="border-primary-dark/60 before:via-accent/30 group relative inline-flex h-11 w-full items-center justify-center gap-2 overflow-hidden rounded-full border bg-white/70 font-mono text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-primary-dark shadow-[0_3px_10px_-4px_rgba(8,39,74,0.12)] backdrop-blur-md transition-all before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:to-transparent before:transition-transform before:duration-1000 hover:border-primary hover:bg-white/85 hover:before:translate-x-full"
									>
										{labels.partners}
										<ArrowRight className="h-4 w-4" />
									</LinkWithChannel>
								</div>

								<hr className="mx-4 my-2 border-border" />

								{/* Support — Site-Design: Trio-Cards in Grid mit Eyebrow */}
								<div className="px-4 pt-5">
									<div className="mb-3 flex items-center gap-2 font-mono text-[0.58rem] font-semibold uppercase tracking-[0.18em] text-primary-hero">
										<span aria-hidden className="bg-primary-hero/60 block h-px w-6" />
										{labels.support}
									</div>
									<div className="grid grid-cols-3 gap-2">
										<LinkWithChannel
											href="/shipping"
											className="hover:border-primary-hero/40 hover:bg-primary-hero/[0.04] group flex flex-col items-center gap-1.5 rounded-xl border border-border bg-card px-2 py-3 transition-all"
										>
											<span className="bg-primary-hero/10 group-hover:bg-primary-hero/20 flex h-9 w-9 items-center justify-center rounded-full transition-colors">
												<Truck className="h-4 w-4 text-primary-hero" />
											</span>
											<span className="font-mono text-[0.55rem] font-medium uppercase tracking-[0.12em] text-foreground">
												{labels.shipping}
											</span>
										</LinkWithChannel>
										<LinkWithChannel
											href="/faq"
											className="hover:border-primary-hero/40 hover:bg-primary-hero/[0.04] group flex flex-col items-center gap-1.5 rounded-xl border border-border bg-card px-2 py-3 transition-all"
										>
											<span className="bg-primary-hero/10 group-hover:bg-primary-hero/20 flex h-9 w-9 items-center justify-center rounded-full transition-colors">
												<HelpCircle className="h-4 w-4 text-primary-hero" />
											</span>
											<span className="font-mono text-[0.55rem] font-medium uppercase tracking-[0.12em] text-foreground">
												{labels.faq}
											</span>
										</LinkWithChannel>
										<LinkWithChannel
											href="/contact"
											className="hover:border-primary-hero/40 hover:bg-primary-hero/[0.04] group flex flex-col items-center gap-1.5 rounded-xl border border-border bg-card px-2 py-3 transition-all"
										>
											<span className="bg-primary-hero/10 group-hover:bg-primary-hero/20 flex h-9 w-9 items-center justify-center rounded-full transition-colors">
												<MessageSquare className="h-4 w-4 text-primary-hero" />
											</span>
											<span className="font-mono text-[0.55rem] font-medium uppercase tracking-[0.12em] text-foreground">
												{labels.contact}
											</span>
										</LinkWithChannel>
									</div>

									{/* Favoriten — Full-width unter Trio */}
									<LinkWithChannel
										href="/favorites"
										className="hover:border-primary-hero/40 hover:bg-primary-hero/[0.04] group mt-3 flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-3 transition-all"
									>
										<span className="bg-primary-hero/10 group-hover:bg-primary-hero/20 flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors">
											<Heart className="h-4 w-4 text-primary-hero" />
										</span>
										<span className="flex-1 font-display text-[0.88rem] font-medium tracking-tight text-foreground">
											{labels.favorites}
										</span>
										<ArrowRight className="text-muted-foreground/50 group-hover:translate-x-0.5 group-hover:text-primary-hero h-4 w-4 transition-all" />
									</LinkWithChannel>
								</div>
							</>
						)}
					</nav>

					{/* Bottom "Alle Peptide"-Button entfernt — top-section hat schon den Peptide-Pill */}
				</MobileMenuContext.Provider>
			</SheetContent>
		</Sheet>
	);
};
